import {
  cacheDecorator,
  invalidateCacheIfSuccessfulDecorator,
  networkFirstAndCacheDecorator,
  updateCache,
} from '@ds/swymkit/tools'

import { swymFetch } from '~/services/fetch-swym'

import type { IWiki, WikiVisibility } from '~/entities/content/wiki/interfaces/wiki'
import type { UserHashKey } from '~/entities/user/user/interfaces/user'

import type { IUpdateParams } from '~/features-shared/content/api/interfaces/params'
import { contentParser } from '~/features-shared/content/api/parser-contents'
import { IContentModel } from '~/features-shared/content/interfaces/content-model'
import type { ISwymContent } from '~/features-shared/content/interfaces/swym-content'
import type { TagActions } from '~/features-shared/content-derived-type/utils/derived-types'
import type { SUBJECT_6W_URI } from '~/features-shared/libs/interfaces/entity'

import type { ICRUDService, ICRUDServiceParams } from '~/interfaces/crud-service'
import type { SwymResponse } from '~/interfaces/services-dto'

interface IWikiSpecificParams {
  /** Parent wiki page subject URI (should be at least the root page). */
  parent_id: SUBJECT_6W_URI

  /**
   * Position of the new page in the parent's children list.
   * @info Valid position: [1 to nbChildren]
   * @info If 0 or undefined, will add the new page at the end of the parent' children list.
   * @default undefined
   */
  position?: number

  /**
   * Should lock/unlock the wiki page edition.
   * @info 0: edition enabled.
   * @info 1: edition disabled.
   */
  locked?: IWiki['locked']

  /**
   * Should lock/unlock the wiki page and all its children (deep).
   * @info "locked" attribute will be ignore if this one is provided.
   */
  lockedAll?: boolean
  visibility?: WikiVisibility
  usersToAdd?: UserHashKey[]
  usersToRemove?: UserHashKey[]
}

/** Parameters to create a wiki */
interface ICreationWikiParams extends Omit<ICRUDServiceParams, 'subject_uri'>, IWikiSpecificParams {
  /** Title of the Wiki */
  title?: string
  /** Community of the wiki, it will be created in this community */
  community_subject_uri?: SUBJECT_6W_URI
  /** Wiki "message body" / description */
  message?: string
  /** Tags to add / remove */
  taggingActions?: TagActions
  /** Is this wiki a draft or not ? */
  published?: boolean
}

/** Parameters to read a wiki */
type IReadWikiParams = Pick<ICRUDServiceParams, 'subject_uri'> & {
  /**
   * Get diff between the provided edition/revision and the previous edition/revision.
   * @info If edition provided, will be the diff with the previous edition.
   * @info If revision provided, will be the diff with the previous revision.
   * @default false
   */
  diff?: boolean

  /**
   * Specific edition index to fetch.
   * @default undefined (current edition, the latest.)
   */
  edition?: number

  /**
   * Specific revision index to fetch.
   * Expected: revision index (and not the edition index).
   * @info To use only to get diff between two revision, else use edition param.
   * @default undefined
   */
  revision?: number

  /** @default true */
  withThumbnailsInfo?: boolean
}

/**
 * Update parameters for Wiki
 * @see ICRUDServiceParams
 * @see ICreationWikiParams
 */
type IUpdateWikiParams = ICRUDServiceParams
  & Omit<ICreationWikiParams, 'published' | 'parent_id'>
  & IUpdateParams
  & Omit<IWikiSpecificParams, 'parent_id'> & {
    minor_edition?: 0 | 1
    is_minor_edit?: 0 | 1
    revision_description?: string
    content_etag?: string
  }

/** Parameters for delete */
interface IDeleteWikiParams extends ICRUDServiceParams {
  mustDeleteSubpages?: boolean
}

type WikiCRUDService = ICRUDService<
  IWiki,
  ICreationWikiParams,
  IReadWikiParams,
  IUpdateWikiParams,
  IDeleteWikiParams
>

/** A CRUD service for wiki */
export const WIKI_CRUD_API: WikiCRUDService = {
  create(params, tracker) {
    return swymFetch.post('/api/WikiTree/addChild', {
      json: {
        params: {
          parent_id: params.parent_id,
          // Do not add position in request object if 0.
          position: params.position || undefined,
          title: params.title,
          community_id: params.community_subject_uri || '',
          message: params.message || '',
          published: params.published ? 1 : 0,
          locked: params.locked,
          lockedAll: params.lockedAll,
          visibility: params.visibility,
          users_to_add: params.usersToAdd,
          users_to_remove: params.usersToRemove,
          minor_edition: '1',
          revision_description: '',
          ...(params.taggingActions ? { taggingActions6w: params.taggingActions } : undefined),
        },
      },
      timeout: 60000,
      tracker,
    })
      .json<SwymResponse<ISwymContent>>()
      .then(contentParser<IWiki>)
      .then(response => updateCache(`wiki-${response.result.subject_uri}`, response))
  },

  read(params, tracker) {
    return cacheDecorator(
      `wiki-${params.subject_uri}-${params.edition}`,
      () => swymFetch
        .post('/api/WikiTree/get_instance', {
          json: {
            params: {
              id: params.subject_uri,
              diff: params.diff,
              edition: params.edition ?? null,
              revision: params.revision ?? null,
              withThumbnailsInfo: params.withThumbnailsInfo,
            },
          },
          tracker,
        })
        .json<SwymResponse<ISwymContent>>()
        .then(contentParser<IWiki>)
        .then((response) => {
          response.result.model = IContentModel.Wiki
          return response
        }),
    )
  },

  update(params, tracker) {
    return networkFirstAndCacheDecorator(
      `wiki-${params.subject_uri}`,
      () => swymFetch
        .post('/api/WikiTree/update', {
          json: {
            params: {
              title: params.title,
              id: params.subject_uri,
              message: params.description,
              withThumbnailsInfo: params.withThumbnailsInfo,
              published: params.published ? 1 : 0,
              ...(params.taggingActions ? { taggingActions6w: params.taggingActions } : undefined),
              minor_edition: params.minor_edition ? 1 : 0,
              is_minor_edit: params.is_minor_edit ? 1 : 0,
              revision_description: params.revision_description,
              content_etag: params.content_etag,
              locked: params.locked,
              lockedAll: params.lockedAll,
              users_to_add: params.usersToAdd,
              users_to_remove: params.usersToRemove,
              visibility: params.visibility,
            },
          },
          tracker,
        })
        .json<SwymResponse<ISwymContent>>()
        .then(contentParser<IWiki>),
    )
  },

  delete(params, tracker) {
    return invalidateCacheIfSuccessfulDecorator(`wiki-${params.subject_uri}`, () => swymFetch
      .post('/api/WikiTree/delete', {
        json: { params: { id: params.subject_uri, mustDeleteSubpages: params.mustDeleteSubpages } },
        tracker,
      })
      .json<SwymResponse<void>>())
  },
}
